import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { packageCharge } from "../../pages/bookingFields";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Priced rows the client asked for that aren't in any package. */
export function CustomAddons({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const patch = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="space-y-2" data-testid="custom-addons">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={r.label ?? ""}
            placeholder="e.g. Champagne on arrival"
            onChange={(e) => patch(i, "label", e.target.value)}
            className="flex-1 bg-input border-border"
            data-testid={`custom-addon-label-${i}`}
          />
          <span className="text-xs text-muted-foreground" aria-hidden="true">$</span>
          <Input
            type="number" step="0.01" min="0"
            value={r.price ?? ""}
            onChange={(e) => patch(i, "price", e.target.value)}
            className="w-28 text-right tnum bg-input border-border"
            aria-label={`Price for ${r.label || `custom add-on ${i + 1}`}`}
            data-testid={`custom-addon-price-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            className="text-muted-foreground hover:text-red-400 p-1"
            aria-label={`Remove ${r.label || `custom add-on ${i + 1}`}`}
            data-testid={`custom-addon-remove-${i}`}
          >
            <Trash size={14} />
          </button>
        </div>
      ))}
      <Button
        type="button" variant="outline" size="sm"
        onClick={() => onChange([...rows, { label: "", price: 0 }])}
        data-testid="custom-addon-add"
      >
        <Plus size={13} className="mr-1.5" /> Add a custom add-on
      </Button>
    </div>
  );
}

/**
 * Package picker for tour bookings. Choosing a package reveals its itinerary;
 * every option is independently selectable (a client can have two restaurant
 * stops) and every pick is copied onto the booking with its price, so editing
 * the package later never reprices a booking already taken.
 */
export function PackagePicker({ form, setForm }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tour-packages")
      .then(({ data }) => setPackages(data || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const selected = packages.find((p) => p.id === form.package_id);
  const picks = Array.isArray(form.package_selections) ? form.package_selections : [];
  const addons = Array.isArray(form.package_addons) ? form.package_addons : [];

  const choose = (id) => {
    if (id === "__none__") {
      setForm((f) => ({
        ...f, package_id: "", package_name: "", package_base: 0,
        package_selections: [], package_addons: [],
      }));
      return;
    }
    const p = packages.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      package_id: id,
      package_name: p?.name || "",
      package_base: Number(p?.base_price) || 0,
      package_selections: [],
      package_addons: [],
      tour_name: f.tour_name?.trim() ? f.tour_name : (p?.name || ""),
    }));
  };

  const toggleOption = (item, opt) => {
    const on = picks.some((s) => s.option_id === opt.id);
    setForm((f) => ({
      ...f,
      package_selections: on
        ? picks.filter((s) => s.option_id !== opt.id)
        : [...picks, {
            item_id: item.id, item_label: item.label,
            option_id: opt.id, option_label: opt.label, price: Number(opt.price) || 0,
          }],
    }));
  };

  const toggleAddon = (addon) => {
    const on = addons.some((a) => a.id === addon.id);
    setForm((f) => ({
      ...f,
      package_addons: on
        ? addons.filter((a) => a.id !== addon.id)
        : [...addons, { id: addon.id, label: addon.label, price: Number(addon.price) || 0 }],
    }));
  };

  return (
    <div className="space-y-4" data-testid="package-picker">
      <Select value={form.package_id || "__none__"} onValueChange={choose}>
        <SelectTrigger className="bg-input border-border" data-testid="package-select">
          <SelectValue placeholder={loading ? "Loading packages…" : "No package — price by the hour"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No package — price by the hour</SelectItem>
          {packages.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`package-option-${p.id}`}>
              {p.name}{Number(p.base_price) > 0 ? ` · ${money(p.base_price)} base` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!loading && packages.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="package-empty-hint">
          No packages built yet — add one under Tours Bookings → Packages.
        </p>
      )}

      {selected && (
        <div className="rounded-2xl border border-border bg-card/40 p-4 space-y-5" data-testid="package-detail">
          <div className="flex items-baseline justify-between gap-3">
            <span className="label-mono">{selected.name}</span>
            <span className="text-sm text-muted-foreground">Base {money(selected.base_price)}</span>
          </div>

          {(selected.itinerary || []).map((item) => (
            <div key={item.id} data-testid={`package-item-${item.id}`}>
              <div className="label-mono mb-2">{item.label}</div>
              <div className="flex flex-wrap gap-2">
                {(item.options || []).map((opt) => {
                  const on = picks.some((s) => s.option_id === opt.id);
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => toggleOption(item, opt)}
                      aria-pressed={on}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                        on ? "border-primary bg-primary/15 text-foreground"
                           : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
                      data-testid={`package-opt-${opt.id}`}
                    >
                      {opt.label}
                      {Number(opt.price) > 0 && <span className="tnum ml-2 text-xs">{money(opt.price)}</span>}
                    </button>
                  );
                })}
                {(item.options || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">No options on this stop.</span>
                )}
              </div>
            </div>
          ))}

          {(selected.addons || []).length > 0 && (
            <div>
              <div className="label-mono mb-2">Package add-ons</div>
              <div className="flex flex-wrap gap-2">
                {selected.addons.map((a) => {
                  const on = addons.some((x) => x.id === a.id);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => toggleAddon(a)}
                      aria-pressed={on}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                        on ? "border-primary bg-primary/15 text-foreground"
                           : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
                      data-testid={`package-addon-${a.id}`}
                    >
                      {a.label}
                      {Number(a.price) > 0 && <span className="tnum ml-2 text-xs">{money(a.price)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-baseline justify-between pt-3 border-t border-border">
            <span className="label-mono">Package charge</span>
            <span className="font-heading text-lg tnum" data-testid="package-charge">{money(packageCharge(form))}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            A package replaces Hours × Rate on this tour. Prices are copied onto the booking,
            so editing the package later won't change what this client was quoted.
          </p>
        </div>
      )}
    </div>
  );
}
